import { Link } from "react-router-dom";
import SEO from "../components/SEO";
import { newsArticles } from "../data/newsArticles";
import { xRoboticsArticles } from "../data/xRoboticsArticles";
import { robotCategories } from "../data/robotCategories";
import { brandIdentity } from "../data/brand";
import { seoConfig } from "../data/seoConfig";
import { languageEditionAlternates } from "../data/siteNavigation";
import { getRenderDate } from "../utils/renderClock";

const resources = [
  { to: "/humanoid-robots", title: "Humanoid robots", description: "Compare robot models, hardware specifications and manufacturers." },
  { to: "/physical-ai", title: "Physical AI", description: "Robot learning, perception, action models and training data." },
  { to: "/robotics-companies", title: "Companies", description: "Find robotics manufacturers by country and product category." },
  { to: "/robotics-events", title: "Events", description: "Conferences, exhibitions and robot competitions." },
];
const guides = [
  { to: "/robot-components", title: "Components", description: "Actuators, transmissions, grippers and power systems." },
  { to: "/robot-sensors", title: "Sensors", description: "Cameras, LiDAR, tactile sensing and feedback." },
  { to: "/robotics-software", title: "Software", description: "Simulation, navigation, planning and robot control." },
  { to: "/robotics-data-collection", title: "Training data", description: "Teleoperation, recorded actions and learning from failures." },
];
const categorySlugs = [
  "humanoid-robots", "robot-dogs-quadrupeds", "industrial-robots",
  "warehouse-robots-amrs", "medical-surgical-robots", "drones-aerial-robots",
];

function ArticleDate({ value }) {
  if (!value) return null;
  const date = new Date(value.length === 10 ? value + "T12:00:00Z" : value);
  if (Number.isNaN(date.getTime())) return null;
  return <time dateTime={value}>{date.toLocaleDateString("en-GB", { day: "numeric", month: "short", year: "numeric", timeZone: "UTC" })}</time>;
}

function StoryMeta({ article }) {
  return <div className="flex flex-wrap items-center gap-x-3 gap-y-1 text-sm text-neutral-600">
    <span className="font-semibold text-blue-700">{article.sponsoredDisclosure ? "Partner content" : article.category || "Robotics"}</span>
    <ArticleDate value={article.datePublished} />
  </div>;
}

export default function Home() {
  const today = getRenderDate().toISOString().slice(0, 10);
  const stories = [...new Map([...newsArticles, ...xRoboticsArticles]
    .filter((article) => article.path && article.title && /^\/robotics(?:-news)?\//.test(article.path)
      && article.datePublished && String(article.datePublished).slice(0, 10) <= today
      && !article.noindex)
    .map((article) => [article.path, article])).values()]
    .sort((a, b) => String(b.datePublished).localeCompare(String(a.datePublished)) || a.path.localeCompare(b.path))
    .slice(0, 7);
  const [lead, ...latest] = stories;
  const categories = categorySlugs.map((slug) => robotCategories.find((category) => category.slug === slug)).filter(Boolean);
  const schema = {
    "@context": "https://schema.org", "@type": "CollectionPage",
    name: "TechniaHQRobot", url: seoConfig.baseUrl + "/",
    description: "Humanoid robot news, Physical AI research and robotics guides.",
    mainEntity: { "@type": "ItemList", numberOfItems: stories.length,
      itemListElement: stories.map((article, index) => ({
        "@type": "ListItem", position: index + 1, name: article.title,
        url: seoConfig.baseUrl + article.path,
      })),
    },
  };

  return <>
    <SEO title="TechniaHQRobot | Humanoid Robots and Physical AI News"
      description="Humanoid robot news, Physical AI research and robotics guides. Explore robot models, hardware, companies and events."
      path="/" alternates={languageEditionAlternates} schema={schema} />

    <section className="site-shell pb-8 pt-9 sm:pb-10 sm:pt-12">
      <p className="text-sm font-semibold text-blue-700">Independent robotics media</p>
      <h1 className="mt-3 max-w-4xl text-4xl font-bold leading-[1.1] tracking-tight text-neutral-950 sm:text-5xl">
        Humanoid robots and Physical AI
      </h1>
      <p className="mt-4 max-w-3xl text-lg leading-8 text-neutral-600">
        Robot launches, research and work on real hardware, with the sources behind each story.
      </p>
    </section>

    {lead && <section className="site-shell pb-12" aria-label="Latest robotics reporting">
      <div className="grid gap-8 border-y border-neutral-200 py-8 lg:grid-cols-[1.15fr_1fr] lg:gap-10">
        <article className="min-w-0">
          {lead.image && <Link to={lead.path} className="focus-ring block overflow-hidden rounded-xl bg-neutral-100" aria-label={lead.title}>
            <img src={lead.image} alt={lead.imageAlt || lead.title}
              width={lead.imageWidth || 1200} height={lead.imageHeight || 675}
              loading="eager" fetchPriority="high" decoding="async"
              className="aspect-video w-full object-contain" />
          </Link>}
          <div className="mt-5"><StoryMeta article={lead} /></div>
          <h2 className="mt-3 text-2xl font-bold leading-tight tracking-tight text-neutral-950 sm:text-3xl">
            <Link to={lead.path} className="focus-ring hover:text-blue-700">{lead.title}</Link>
          </h2>
          <p className="mt-4 text-base leading-8 text-neutral-700">{lead.description || lead.heroSubtitle}</p>
        </article>
        <div>
          <div className="flex items-baseline justify-between gap-4 border-b border-neutral-200 pb-3">
            <h2 className="text-lg font-bold text-neutral-950">Latest stories</h2>
            <Link to="/robotics-news" className="focus-ring text-sm font-semibold text-blue-700 underline underline-offset-4">All news</Link>
          </div>
          <div className="divide-y divide-neutral-200">
            {latest.map((article) => <article key={article.path} className="py-5">
              <StoryMeta article={article} />
              <h3 className="mt-2 text-lg font-bold leading-7 text-neutral-950">
                <Link to={article.path} className="focus-ring hover:text-blue-700">{article.title}</Link>
              </h3>
            </article>)}
          </div>
        </div>
      </div>
    </section>}

    <section className="site-shell pb-12" aria-labelledby="explore-robotics">
      <h2 id="explore-robotics" className="text-2xl font-bold tracking-tight text-neutral-950">Explore robotics</h2>
      <div className="mt-6 grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
        {resources.map((item) => <article key={item.to} className="rounded-xl border border-neutral-200 bg-white p-5">
          <h3 className="text-xl font-bold"><Link to={item.to} className="focus-ring text-neutral-950 hover:text-blue-700">{item.title}</Link></h3>
          <p className="mt-3 text-base leading-7 text-neutral-600">{item.description}</p>
        </article>)}
      </div>
    </section>

    <section className="border-y border-neutral-200 bg-white py-10 sm:py-12" aria-labelledby="hardware-software">
      <div className="site-shell grid gap-8 lg:grid-cols-[.65fr_1.35fr]">
        <div>
          <h2 id="hardware-software" className="text-2xl font-bold tracking-tight text-neutral-950">Inside the robot</h2>
          <p className="mt-4 text-base leading-8 text-neutral-600">Guides to the hardware, software and data behind robot movement.</p>
          <Link to="/robotics" className="focus-ring mt-5 inline-block text-sm font-semibold text-blue-700 underline underline-offset-4">Browse all guides</Link>
        </div>
        <div className="grid gap-x-8 gap-y-6 sm:grid-cols-2">
          {guides.map((item) => <article key={item.to} className="border-t border-neutral-200 pt-4">
            <h3 className="text-lg font-bold"><Link to={item.to} className="focus-ring hover:text-blue-700">{item.title}</Link></h3>
            <p className="mt-2 text-base leading-7 text-neutral-600">{item.description}</p>
          </article>)}
        </div>
      </div>
    </section>

    <section className="site-shell py-10 sm:py-12" aria-labelledby="robot-categories">
      <div className="flex flex-wrap items-baseline justify-between gap-4">
        <h2 id="robot-categories" className="text-2xl font-bold tracking-tight text-neutral-950">Robot categories</h2>
        <Link to="/robot-types" className="focus-ring text-sm font-semibold text-blue-700 underline underline-offset-4">All categories</Link>
      </div>
      <div className="mt-6 grid gap-x-8 gap-y-6 sm:grid-cols-2 lg:grid-cols-3">
        {categories.map((category) => <article key={category.slug} className="border-t border-neutral-200 pt-4">
          <h3 className="text-lg font-bold"><Link to={"/robot-types/" + category.slug} className="focus-ring hover:text-blue-700">{category.name}</Link></h3>
          <p className="mt-2 text-base leading-7 text-neutral-600">{category.description || category.definition}</p>
        </article>)}
      </div>
    </section>

    <section className="site-shell pb-8">
      <div className="flex flex-col gap-5 rounded-xl bg-neutral-950 p-6 text-white sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h2 className="text-xl font-bold">{brandIdentity.name}</h2>
          <p className="mt-2 max-w-2xl text-base leading-7 text-neutral-300">Read about the publication, its sourcing practices and how to request a correction.</p>
        </div>
        <div className="flex shrink-0 flex-wrap gap-4 text-sm font-semibold">
          <Link to="/about-techniahq" className="focus-ring underline underline-offset-4">About</Link>
          <Link to="/editorial-policy" className="focus-ring underline underline-offset-4">Editorial policy</Link>
          <Link to="/contact" className="focus-ring underline underline-offset-4">Contact</Link>
          <a href="/sitemap.html" className="focus-ring underline underline-offset-4">Site map</a>
        </div>
      </div>
    </section>
  </>;
}
